<template>
    <Page>
        <Container class="max-w-screen-lg">
            <div class="mb-6 flex items-center justify-between gap-4">
                <Title class="!mb-0">Feature Ideas</Title>
                <button
                    class="rounded bg-neutral-200 px-3 py-2 text-sm font-semibold dark:bg-neutral-800"
                    type="button"
                    @click="loadIdeas"
                >
                    Refresh
                </button>
            </div>

            <div class="mb-4 flex flex-wrap gap-2">
                <button
                    v-for="filter in filters"
                    :key="filter.key"
                    class="rounded border px-3 py-2 text-sm font-semibold"
                    :class="activeFilter === filter.key
                        ? 'border-brand-primary-600 bg-brand-primary-600 text-white'
                        : 'border-neutral-300 text-neutral-700 dark:border-neutral-700 dark:text-neutral-200'"
                    type="button"
                    @click="activeFilter = filter.key"
                >
                    {{ filter.label }}
                    <span class="ml-1 text-xs opacity-75">{{ filterCount(filter) }}</span>
                </button>
            </div>

            <Loading v-if="loading" />

            <div v-else-if="visibleIdeas.length === 0" class="rounded bg-neutral-100 p-4 text-sm text-neutral-600 dark:bg-neutral-800 dark:text-neutral-300">
                No feature ideas have been submitted yet.
            </div>

            <div v-else class="space-y-4">
                <div
                    v-for="idea in visibleIdeas"
                    :key="idea.id"
                    class="rounded bg-neutral-100 p-4 dark:bg-neutral-800"
                >
                    <div class="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
                        <div class="min-w-0 flex-1">
                            <div class="mb-2 flex flex-wrap items-center gap-2">
                                <span class="rounded bg-brand-primary-100 px-2 py-1 text-xs font-semibold text-brand-primary-800 dark:bg-brand-primary-900 dark:text-brand-primary-100">
                                    {{ statusLabel(idea.status) }}
                                </span>
                                <span v-if="idea.category" class="rounded bg-neutral-200 px-2 py-1 text-xs font-semibold text-neutral-700 dark:bg-neutral-700 dark:text-neutral-200">
                                    {{ categoryLabel(idea.category) }}
                                </span>
                                <span class="text-xs text-neutral-500 dark:text-neutral-400">
                                    {{ formatDate(idea.submitted_at || idea.created_at) }}
                                </span>
                                <span class="rounded px-2 py-1 text-xs font-semibold"
                                      :class="isVotable(idea)
                                        ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-100'
                                        : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-100'"
                                >
                                    {{ isVotable(idea) ? 'Visible for voting' : 'Internal review' }}
                                </span>
                            </div>

                            <label class="mb-1 block text-xs font-semibold text-neutral-500 dark:text-neutral-400">
                                Public title
                            </label>
                            <input
                                class="mb-3 w-full rounded border border-neutral-300 bg-white px-3 py-2 text-sm text-neutral-900 dark:border-neutral-700 dark:bg-neutral-950 dark:text-white"
                                placeholder="Required before voting"
                                type="text"
                                :disabled="updatingId === idea.id"
                                :value="idea.title"
                                @change="updateIdea(idea, {title: $event.target.value})"
                            />

                            <label class="mb-1 block text-xs font-semibold text-neutral-500 dark:text-neutral-400">
                                Reviewed copy
                            </label>
                            <textarea
                                class="min-h-[84px] w-full rounded border border-neutral-300 bg-white px-3 py-2 text-sm leading-6 text-neutral-900 dark:border-neutral-700 dark:bg-neutral-950 dark:text-white"
                                :disabled="updatingId === idea.id"
                                :value="idea.description"
                                @change="updateIdea(idea, {description: $event.target.value})"
                            ></textarea>

                            <div class="mt-3 text-xs text-neutral-500 dark:text-neutral-400">
                                <span v-if="idea.user">
                                    {{ idea.user.first_name }} {{ idea.user.last_name }} &lt;{{ idea.user.email }}&gt;
                                </span>
                                <span v-else>Unknown submitter</span>
                                <span class="mx-2">/</span>
                                <span>{{ idea.up_votes_count || 0 }} up</span>
                                <span class="mx-1">/</span>
                                <span>{{ idea.down_votes_count || 0 }} down</span>
                            </div>

                            <div v-if="errors[idea.id]" class="mt-3 text-sm text-red-600 dark:text-red-400">
                                {{ errors[idea.id] }}
                            </div>
                        </div>

                        <div class="w-full space-y-3 md:w-64">
                            <div>
                                <label class="mb-1 block text-xs font-semibold text-neutral-500 dark:text-neutral-400">
                                    Category
                                </label>
                                <select
                                    class="w-full rounded border border-neutral-300 bg-white px-3 py-2 text-sm dark:border-neutral-700 dark:bg-neutral-950"
                                    :value="idea.category"
                                    :disabled="updatingId === idea.id"
                                    @change="updateIdea(idea, {category: $event.target.value || null})"
                                >
                                    <option value="">Uncategorized</option>
                                    <option v-for="category in categories" :key="category.key" :value="category.key">
                                        {{ category.label }}
                                    </option>
                                </select>
                            </div>

                            <div>
                            <label class="mb-1 block text-xs font-semibold text-neutral-500 dark:text-neutral-400">
                                Status
                            </label>
                            <select
                                class="w-full rounded border border-neutral-300 bg-white px-3 py-2 text-sm dark:border-neutral-700 dark:bg-neutral-950"
                                :value="idea.status"
                                :disabled="updatingId === idea.id"
                                @change="updateStatus(idea, $event.target.value)"
                            >
                                <option v-for="status in statuses" :key="status.key" :value="status.key">
                                    {{ status.label }}
                                </option>
                            </select>
                            </div>

                            <div v-if="idea.status === 'duplicate' || idea.canonical_feature_idea_id">
                                <label class="mb-1 block text-xs font-semibold text-neutral-500 dark:text-neutral-400">
                                    Canonical idea
                                </label>
                                <select
                                    class="w-full rounded border border-neutral-300 bg-white px-3 py-2 text-sm dark:border-neutral-700 dark:bg-neutral-950"
                                    :value="idea.canonical_feature_idea_id"
                                    :disabled="updatingId === idea.id"
                                    @change="updateIdea(idea, {canonical_feature_idea_id: $event.target.value || null})"
                                >
                                    <option value="">Choose original</option>
                                    <option
                                        v-for="candidate in canonicalCandidates(idea)"
                                        :key="candidate.id"
                                        :value="candidate.id"
                                    >
                                        {{ ideaLabel(candidate) }}
                                    </option>
                                </select>
                            </div>

                            <div class="grid grid-cols-2 gap-2">
                                <button
                                    class="rounded bg-neutral-200 px-3 py-2 text-xs font-semibold dark:bg-neutral-700"
                                    type="button"
                                    :disabled="updatingId === idea.id"
                                    @click="updateStatus(idea, 'reviewing')"
                                >
                                    Review
                                </button>
                                <button
                                    class="rounded bg-green-600 px-3 py-2 text-xs font-semibold text-white"
                                    type="button"
                                    :disabled="updatingId === idea.id"
                                    @click="updateStatus(idea, 'accepted')"
                                >
                                    Approve
                                </button>
                                <button
                                    class="rounded bg-yellow-600 px-3 py-2 text-xs font-semibold text-white"
                                    type="button"
                                    :disabled="updatingId === idea.id"
                                    @click="updateStatus(idea, 'duplicate')"
                                >
                                    Duplicate
                                </button>
                                <button
                                    class="rounded bg-red-700 px-3 py-2 text-xs font-semibold text-white"
                                    type="button"
                                    :disabled="updatingId === idea.id"
                                    @click="updateStatus(idea, 'rejected')"
                                >
                                    Reject
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Container>
    </Page>
</template>

<script>
import {defineComponent} from 'vue';
import {mapActions} from 'vuex';
import moment from 'moment-timezone';

const categories = [
  {key: 'workout_logging', label: 'Workout logging'},
  {key: 'analytics', label: 'Analytics'},
  {key: 'progress_photos', label: 'Progress photos'},
  {key: 'templates', label: 'Templates'},
  {key: 'integrations', label: 'Integrations'},
  {key: 'coaching', label: 'Coaching'},
  {key: 'other', label: 'Other'},
];

const statuses = [
  {key: 'new', label: 'New'},
  {key: 'reviewing', label: 'Reviewing'},
  {key: 'accepted', label: 'Accepted'},
  {key: 'duplicate', label: 'Duplicate'},
  {key: 'planned', label: 'Planned'},
  {key: 'building', label: 'Building'},
  {key: 'shipped', label: 'Shipped'},
  {key: 'rejected', label: 'Rejected'},
];

const filters = [
  {key: 'needs_review', label: 'Needs review', statuses: ['new', 'reviewing']},
  {key: 'votable', label: 'Voting', statuses: ['accepted', 'planned', 'building']},
  {key: 'duplicates', label: 'Duplicates', statuses: ['duplicate']},
  {key: 'closed', label: 'Closed', statuses: ['shipped', 'rejected']},
  {key: 'all', label: 'All', statuses: null},
];

const votableStatuses = ['accepted', 'planned', 'building'];

export default defineComponent({
  name: 'FeatureIdeas',
  created() {
    this.loadIdeas();
  },
  data() {
    return {
      activeFilter: 'needs_review',
      categories,
      errors: {},
      filters,
      ideas: [],
      loading: false,
      statuses,
      updatingId: null,
    };
  },
  computed: {
    visibleIdeas() {
      const filter = filters.find(item => item.key === this.activeFilter);

      if (!filter || !filter.statuses) {
        return this.ideas;
      }

      return this.ideas.filter(idea => filter.statuses.includes(idea.status));
    },
  },
  methods: {
    ...mapActions({
      adminFeatureIdeas: 'admin/featureIdeas',
      adminUpdateFeatureIdea: 'admin/updateFeatureIdea',
    }),
    categoryLabel(category) {
      return categories.find(item => item.key === category)?.label || category;
    },
    formatDate(dateString) {
      if (!dateString) {
        return '';
      }

      return moment(dateString).fromNow();
    },
    canonicalCandidates(idea) {
      return this.ideas.filter(candidate => candidate.id !== idea.id
        && candidate.status !== 'duplicate'
        && !candidate.canonical_feature_idea_id);
    },
    filterCount(filter) {
      if (!filter.statuses) {
        return this.ideas.length;
      }

      return this.ideas.filter(idea => filter.statuses.includes(idea.status)).length;
    },
    ideaLabel(idea) {
      return idea.title || idea.description?.slice(0, 80) || `Idea ${idea.id}`;
    },
    isVotable(idea) {
      return votableStatuses.includes(idea.status);
    },
    loadIdeas() {
      this.loading = true;

      return this.adminFeatureIdeas().then((response) => {
        this.ideas = response.data.data || [];
      }).finally(() => {
        this.loading = false;
      });
    },
    statusLabel(status) {
      return statuses.find(item => item.key === status)?.label || status;
    },
    updateIdea(idea, payload) {
      this.errors = {
        ...this.errors,
        [idea.id]: null,
      };
      this.updatingId = idea.id;

      return this.adminUpdateFeatureIdea({
        id: idea.id,
        ...payload,
      }).then((response) => {
        const updated = response.data.data;
        this.ideas = this.ideas.map(item => item.id === updated.id ? updated : item);
      }).catch((error) => {
        this.errors = {
          ...this.errors,
          [idea.id]: error.response?.data?.errors?.[0]?.detail || error.response?.data?.message || 'Could not update this idea.',
        };
      }).finally(() => {
        this.updatingId = null;
      });
    },
    updateStatus(idea, status) {
      return this.updateIdea(idea, {
        canonical_feature_idea_id: idea.canonical_feature_idea_id,
        status,
      });
    },
  },
});
</script>
